import React from "react";
import { Link } from "react-router-dom";
import { Instagram, MessageCircle, Facebook, Mail, Phone, MapPin, Code2 } from "lucide-react";
import { useContent } from "../lib/content";
import { buildWhatsAppLink } from "../lib/api";

export default function Footer() {
  const { settings } = useContent();

  return (
    <footer className="bg-stone-900 text-stone-300 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-2xl honey-gradient flex items-center justify-center">
              <span className="text-white font-black text-lg">
                {(settings.brand_name_fa || "تحسین").charAt(0)}
              </span>
            </div>
            <div>
              <div className="text-xl font-black text-white" data-testid="footer-brand-fa">
                {settings.brand_name_fa || "تحسین"}
              </div>
              <div className="text-[11px] tracking-widest text-amber-400 uppercase" data-testid="footer-brand-en">
                {settings.brand_name_en || "Tahsin"}
              </div>
            </div>
          </div>
          <p className="text-sm text-stone-400 leading-relaxed" data-testid="footer-tagline">
            {settings.tagline ||
              "فروشگاه تحسین در کابل — عسل خالص، زعفران سرگل هرات و محصولات طبیعی از سراسر افغانستان."}
          </p>
        </div>

        <div>
          <h4 className="text-white font-bold mb-5">دسترسی سریع</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/products" className="hover:text-amber-400 transition-colors" data-testid="footer-link-products">کاتالوگ محصولات</Link></li>
            <li><Link to="/articles" className="hover:text-amber-400 transition-colors" data-testid="footer-link-articles">مقالات</Link></li>
            <li><Link to="/about" className="hover:text-amber-400 transition-colors" data-testid="footer-link-about">درباره ما</Link></li>
            <li><Link to="/contact" className="hover:text-amber-400 transition-colors" data-testid="footer-link-contact">تماس</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold mb-5">دسته‌بندی‌ها</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/products?category=honey" className="hover:text-amber-400 transition-colors">عسل طبیعی</Link></li>
            <li><Link to="/products?category=saffron" className="hover:text-amber-400 transition-colors">زعفران هرات</Link></li>
            <li><Link to="/products?category=dried_fruit" className="hover:text-amber-400 transition-colors">میوه خشک</Link></li>
            <li><Link to="/products?category=other" className="hover:text-amber-400 transition-colors">سایر محصولات</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold mb-5">ارتباط با ما</h4>
          <ul className="space-y-3 text-sm">
            {settings.contact_phone && (
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-amber-400" />
                <span className="persian-numerals">{settings.contact_phone}</span>
              </li>
            )}
            {settings.contact_phone_2 && (
              <li className="flex items-center gap-2 text-stone-400">
                <Phone className="w-4 h-4 text-amber-400/70" />
                <span className="persian-numerals">{settings.contact_phone_2}</span>
              </li>
            )}
            {settings.contact_email && (
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-amber-400" />
                <a href={`mailto:${settings.contact_email}`}>{settings.contact_email}</a>
              </li>
            )}
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-amber-400 mt-1 shrink-0" />
              <span>{settings.address || "کابل، افغانستان"}</span>
            </li>
          </ul>

          <div className="flex items-center gap-3 mt-6">
            {settings.instagram_url && (
              <a
                href={settings.instagram_url}
                target="_blank"
                rel="noreferrer"
                aria-label="اینستاگرام"
                className="w-10 h-10 rounded-full bg-stone-800 hover:bg-amber-600 flex items-center justify-center transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
            )}
            <a
              href={buildWhatsAppLink(settings.whatsapp_number, "سلام، درباره محصولات تحسین سوال داشتم.")}
              target="_blank"
              rel="noreferrer"
              aria-label="واتساپ"
              className="w-10 h-10 rounded-full bg-stone-800 hover:bg-[#25D366] flex items-center justify-center transition-colors"
              data-testid="footer-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
            </a>
            {settings.facebook_url && (
              <a
                href={settings.facebook_url}
                target="_blank"
                rel="noreferrer"
                aria-label="فیسبوک"
                className="w-10 h-10 rounded-full bg-stone-800 hover:bg-[#1877F2] flex items-center justify-center transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-stone-500">
          <span>© {settings.brand_name_fa || "تحسین"} · کابل، افغانستان. تمام حقوق محفوظ است.</span>
          <div className="flex items-center gap-5">
            {settings.developer_url && (
              <a
                href={settings.developer_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 hover:text-amber-400 transition-colors"
                data-testid="footer-developer-link"
              >
                <Code2 className="w-3 h-3" />
                <span>
                  ساخت و توسعه:{" "}
                  <span className="text-stone-300">{settings.developer_name || "Murtaza Mahdiyar"}</span>
                </span>
              </a>
            )}
            <Link to="/admin" className="hover:text-amber-400 transition-colors" data-testid="footer-admin-link">
              ورود مدیر
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
